import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  NETWORK_ERROR_SNACKBAR_KEY,
  findBackendErrorCode,
  getErrorKind,
} from '@/shared/lib/errors';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';

import { DRIVE_ERROR_CODES, resolveDriveError } from '../model/driveErrors';

/**
 * Hook-level `onError` for Drive writes. A hook-level `onError` replaces the QueryClient default, so this keeps what
 * that default does besides the raw message: a 401 stays silent, a 403 with a Drive code says which refusal it is
 * (a bare one shows the generic forbidden message), and an unreachable server shows the one network snackbar.
 */
export const useDriveErrorToast = (): ((error: unknown) => void) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  return useCallback(
    (error: unknown) => {
      const kind = getErrorKind(error);
      if (kind === 'unauthorized') return;
      if (kind === 'network') {
        enqueueSnackbar(t('errors.network'), {
          variant: 'error',
          key: NETWORK_ERROR_SNACKBAR_KEY,
          preventDuplicate: true,
        });

        return;
      }
      const known = findBackendErrorCode(error, DRIVE_ERROR_CODES).code !== undefined;
      const message =
        !known && kind === 'forbidden' ? t('errors.action_forbidden') : resolveDriveError(error, t);
      enqueueSnackbar(message, { variant: 'error' });
    },
    [enqueueSnackbar, t],
  );
};
